import { useCallback, useEffect, useState } from 'react'
import type { IWalletKit, WalletKitTypes } from '@reown/walletkit'
import type { SessionTypes } from '@walletconnect/types'
import { getWalletKit } from '../lib/walletkit.js'
import { CHAIN_ID, MAIN_SAFE } from '../lib/constants.js'
import { proxyRpc } from '../lib/rpcProxy.js'
import {
  getCapabilitiesFor,
  handleGetCallsStatus,
} from '../lib/handler.js'

export type Proposal = WalletKitTypes.SessionProposal
export type Request = WalletKitTypes.SessionRequest

/**
 * Methods we transparently auto-respond to without bothering the user.
 * Anything not in here OR in `PROXY_READ_METHODS` falls through to the
 * pendingRequests state and shows a sign-request card.
 */
const AUTO_RESPOND_LOCAL = new Set<string>([
  'eth_accounts',
  'eth_chainId',
  'net_version',
  'wallet_getCapabilities',
  'wallet_getCallsStatus',
  'wallet_showCallsStatus',
  'wallet_switchEthereumChain',
  'wallet_addEthereumChain',
])

/**
 * Read methods we proxy to a public RPC node. WC v2's UniversalProvider
 * routes all eth_* through the wallet by default; if we don't handle these
 * the dApp's preflight (estimateGas, balance, nonce checks) errors out
 * with the opaque "Unknown RPC error" we kept seeing on Jumper.
 */
const PROXY_READ_METHODS = new Set<string>([
  'eth_call',
  'eth_estimateGas',
  'eth_getBalance',
  'eth_getCode',
  'eth_getTransactionCount',
  'eth_blockNumber',
  'eth_getBlockByNumber',
  'eth_getBlockByHash',
  'eth_getTransactionByHash',
  'eth_getTransactionReceipt',
  'eth_getStorageAt',
  'eth_getLogs',
  'eth_gasPrice',
  'eth_maxPriorityFeePerGas',
  'eth_feeHistory',
])

export interface WalletKitState {
  ready: boolean
  sessions: SessionTypes.Struct[]
  pendingProposals: Proposal[]
  pendingRequests: Request[]
  error: string | null
}

export function useWalletKitState(): WalletKitState & {
  refreshSessions: () => Promise<void>
  removeProposal: (id: number) => void
  removeRequest: (topic: string, id: number) => void
} {
  const [ready, setReady] = useState(false)
  const [sessions, setSessions] = useState<SessionTypes.Struct[]>([])
  const [pendingProposals, setProposals] = useState<Proposal[]>([])
  const [pendingRequests, setRequests] = useState<Request[]>([])
  const [error, setError] = useState<string | null>(null)

  const refreshSessions = useCallback(async () => {
    const wk = await getWalletKit()
    setSessions(Object.values(wk.getActiveSessions()))
  }, [])

  const removeProposal = useCallback((id: number) => {
    setProposals((prev) => prev.filter((p) => p.id !== id))
  }, [])

  const removeRequest = useCallback((topic: string, id: number) => {
    setRequests((prev) =>
      prev.filter((r) => !(r.topic === topic && r.id === id)),
    )
  }, [])

  useEffect(() => {
    let mounted = true
    let wk: IWalletKit | null = null

    const onProposal = (p: Proposal) => {
      console.log(`[safeward ${stamp()}] session_proposal`, {
        id: p.id,
        proposer: p.params.proposer.metadata.name,
        requiredChains: p.params.requiredNamespaces?.eip155?.chains,
        optionalChains: p.params.optionalNamespaces?.eip155?.chains,
      })
      setProposals((prev) => [...prev, p])
    }
    const onRequest = (r: Request) => {
      const method = r.params.request.method
      const t0 = Date.now()
      console.log(`[safeward ${stamp(t0)}] session_request`, {
        method,
        chainId: r.params.chainId,
        id: r.id,
        topic: r.topic.slice(0, 12) + '…',
      })
      if (AUTO_RESPOND_LOCAL.has(method)) {
        void autoRespondLocal(r, t0)
        return
      }
      if (PROXY_READ_METHODS.has(method)) {
        void autoRespondProxy(r, t0)
        return
      }
      setRequests((prev) => [...prev, r])
      console.log(`[safeward ${stamp()}] queued pendingRequests +${Date.now() - t0}ms`)
    }
    const onDelete = () => void refreshSessions()

    void getWalletKit()
      .then((instance) => {
        if (!mounted) return
        wk = instance
        wk.on('session_proposal', onProposal)
        wk.on('session_request', onRequest)
        wk.on('session_delete', onDelete)
        setSessions(Object.values(wk.getActiveSessions()))
        setReady(true)
      })
      .catch((err: Error) => {
        if (!mounted) return
        setError(err.message)
      })

    return () => {
      mounted = false
      if (wk) {
        wk.off('session_proposal', onProposal)
        wk.off('session_request', onRequest)
        wk.off('session_delete', onDelete)
      }
    }
  }, [refreshSessions])

  return {
    ready,
    sessions,
    pendingProposals,
    pendingRequests,
    error,
    refreshSessions,
    removeProposal,
    removeRequest,
  }
}

function stamp(t = Date.now()): string {
  const d = new Date(t)
  return `${d.toLocaleTimeString('en-US', { hour12: false })}.${String(d.getMilliseconds()).padStart(3, '0')}`
}

/**
 * Auto-respond to method calls we can answer locally without an external
 * round-trip. Capabilities, call status, chain-management, etc.
 */
async function autoRespondLocal(req: Request, t0: number): Promise<void> {
  const wk = await getWalletKit()
  const { topic, id, params } = req
  const method = params.request.method
  try {
    let result: unknown
    if (method === 'eth_accounts') {
      result = [MAIN_SAFE]
    } else if (method === 'eth_chainId' || method === 'net_version') {
      // Strip "eip155:" prefix from session_request's chainId field. dApps
      // ask per-chain so we honor whatever they're requesting.
      const num = Number(params.chainId.split(':').pop() ?? CHAIN_ID)
      result = method === 'eth_chainId' ? `0x${num.toString(16)}` : String(num)
    } else if (method === 'wallet_getCapabilities') {
      const address = params.request.params?.[0] as string | undefined
      result = address ? getCapabilitiesFor(address) : {}
    } else if (method === 'wallet_getCallsStatus') {
      const arg = params.request.params?.[0]
      const callId =
        typeof arg === 'string'
          ? arg
          : ((arg as { id?: string } | undefined)?.id ?? '')
      if (!callId) throw new Error('wallet_getCallsStatus: missing id')
      result = await handleGetCallsStatus(callId)
    } else if (method === 'wallet_showCallsStatus') {
      result = null
    } else if (method === 'wallet_switchEthereumChain') {
      const target = (params.request.params?.[0] as { chainId?: string })?.chainId
      const wantHex = `0x${CHAIN_ID.toString(16)}`
      if (!target || target.toLowerCase() !== wantHex.toLowerCase()) {
        throw new Error(
          `SafeWard only supports chain ${wantHex}; refusing switch to ${target}`,
        )
      }
      result = null
    } else if (method === 'wallet_addEthereumChain') {
      throw new Error('SafeWard only operates on Ethereum mainnet')
    } else {
      result = null
    }
    await wk.respondSessionRequest({
      topic,
      response: { id, jsonrpc: '2.0', result: result ?? null },
    })
    console.log(
      `[safeward ${stamp()}] auto-respond ${method} (+${Date.now() - t0}ms)`,
    )
  } catch (err) {
    const msg = (err as Error).message
    const isUnknownBundle = msg.startsWith('Unknown bundle id')
    if (isUnknownBundle) {
      console.warn(
        `[safeward ${stamp()}] ${method} → unknown id (told dApp to stop) (+${Date.now() - t0}ms)`,
      )
    } else {
      console.error(
        `[safeward ${stamp()}] ${method} failed (+${Date.now() - t0}ms)`,
        err,
      )
    }
    await wk.respondSessionRequest({
      topic,
      response: {
        id,
        jsonrpc: '2.0',
        error: {
          code: isUnknownBundle ? 5300 : 5000,
          message: msg,
        },
      },
    })
  }
}

/**
 * Forward the request to a public RPC node for the chain in question and
 * return its response unchanged. We don't second-guess these — they're
 * preflight reads (gas / balance / receipts) that the dApp drives.
 */
async function autoRespondProxy(req: Request, t0: number): Promise<void> {
  const wk = await getWalletKit()
  const { topic, id, params } = req
  const method = params.request.method
  try {
    const result = await proxyRpc(
      params.chainId,
      method,
      params.request.params as unknown[],
    )
    await wk.respondSessionRequest({
      topic,
      response: { id, jsonrpc: '2.0', result: result ?? null },
    })
    console.log(
      `[safeward ${stamp()}] proxied ${method} (+${Date.now() - t0}ms)`,
    )
  } catch (err) {
    console.error(
      `[safeward ${stamp()}] proxy ${method} failed (+${Date.now() - t0}ms)`,
      err,
    )
    await wk.respondSessionRequest({
      topic,
      response: {
        id,
        jsonrpc: '2.0',
        error: { code: 5000, message: (err as Error).message },
      },
    })
  }
}
